import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { commentsAPI } from '../services/api';
import Comment from './Comment';
import toast from 'react-hot-toast';
import { FaComments } from 'react-icons/fa';

const CommentSection = ({ promptId }) => {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptId]);

  const fetchComments = async () => {
    try {
      const response = await commentsAPI.getByPromptId(promptId);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Fetch comments error:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      toast.error('Please login to comment');
      return;
    }

    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    setSubmitting(true);
    try {
      await commentsAPI.create({
        promptId,
        content: newComment.trim()
      });
      toast.success('Comment posted!');
      setNewComment('');
      await fetchComments(); // Refresh comments
    } catch (error) {
      console.error('Submit comment error:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentCommentId, content) => {
    if (!currentUser) {
      toast.error('Please login to reply');
      return;
    }

    try {
      await commentsAPI.create({
        promptId,
        parentCommentId,
        content: content.trim()
      });
      toast.success('Reply posted!');
      await fetchComments(); // Refresh to show reply
    } catch (error) {
      console.error('Submit reply error:', error);
      toast.error('Failed to post reply');
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-spooky-bg-medium rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500 dark:text-gray-400">
          Loading comments...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-spooky-bg-medium rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-spooky-ghost-white flex items-center">
          <FaComments className="mr-2 text-boop-primary dark:text-spooky-ghost-glow" />
          Comments ({comments.length})
        </h3>
      </div>

      {/* New Comment Form */}
      {currentUser ? (
        <form onSubmit={handleSubmitComment} className="mb-6">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            className="w-full px-4 py-3 border rounded-lg dark:bg-spooky-bg-dark dark:border-spooky-purple-mist dark:text-spooky-ghost-white focus:ring-2 focus:ring-boop-primary"
            rows="3"
            maxLength={2000}
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {newComment.length}/2000
            </span>
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="px-6 py-2 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary disabled:bg-gray-400 disabled:cursor-not-allowed transition"
            >
              {submitting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-spooky-bg-dark rounded-lg text-center">
          <p className="text-gray-600 dark:text-gray-400">
            Please <a href="/login" className="text-boop-primary dark:text-spooky-ghost-glow hover:underline">login</a> to comment
          </p>
        </div>
      )}

      {/* Comments List */}
      {comments.length === 0 ? (
        <div className="text-center py-8">
          <FaComments className="text-5xl text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            No comments yet. Be the first to share your thoughts!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              onReply={handleReply}
              onUpdate={fetchComments}
              onDelete={fetchComments}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentSection;
